// noinspection PointlessBooleanExpressionJS
import type { DateTime } from "luxon";
import { convertDateTimesToString } from "utils/apiHelpers";
import type { EntityHistoryReferenceDto } from "client/api/EntityHistoryReferenceDto";
import axiosClient from "../axios-client";
import type { EntityDto } from "./EntityDto";
import type { EntitlementDto, ExpiringFeatureDto, IntLimitFeatureDto, OverrunLimitFeatureDto } from "./FeatureApi";

export interface ServerLicensingChannelDto {
  id: string;
  createdDate: DateTime;
  expiryDate: DateTime | null;
  nextRenewalDate: DateTime | null;
  trialId: string | null;
  salesforceContractId: string | null;
  zuoraSubscriptionKey: string | null;
  cancelAtPeriodEnd: boolean | null;
  subscriptionId: string | null;
  planName: string | null;
  status: string | null;
}

export interface ServerLicenseDto extends EntityDto {
  id: string;
  serial: string;
  serialHash: string;
  validFromDate: DateTime;
  maintenanceExpiresDate: DateTime;
  expiryDate: DateTime;
  salesforceContractId: string;
  licensingChannel: ServerLicensingChannelDto;
  isFulfilled: boolean;
  friendlyName?: string;
  entitlements: Record<string, EntitlementDto>;
}

export const PtmMachineTypes: string[] = ["TentaclePassive", "TentacleActive", "Ssh"];

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function isIntLimitLicenseFeatureDto(x: any): x is IntLimitFeatureDto {
  return x.hasOwnProperty("isUnlimited");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isExpiringFeatureDto(x: any): x is ExpiringFeatureDto {
  return x.hasOwnProperty("expiryDate") || x.hasOwnProperty("neverExpires");
}

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function isOverrunLimitLicenseFeatureDto(x: any): x is OverrunLimitFeatureDto {
  return x.hasOwnProperty("isUnlimited") && x.hasOwnProperty("overrun");
}

const BASE_URL = "server-licenses";

export const getServerLicenseById = async (licenseId: string): Promise<ServerLicenseDto> => {
  const response = await axiosClient.get<ServerLicenseDto>(`${BASE_URL}/${licenseId}`);
  return response.data;
};

export const getServerLicenseSnapshot = async (licenseId: string, asOf: DateTime): Promise<Record<string, unknown>> => {
  const response = await axiosClient.get<Record<string, unknown>>(`${BASE_URL}/${licenseId}/snapshot`, {
    params: { asOf: asOf.toUTC().toISO() },
  });
  return response.data;
};

export const getServerLicenseExistsById = async (licenseId: string): Promise<boolean> => {
  const response = await axiosClient.head(`${BASE_URL}/${licenseId}`, { validateStatus: undefined });
  return response.status === 200;
};

export interface LicenseXmlDto {
  id: string;
  licenseXml: string;
}

export type ServerLicenseXmlGenerationMode = "Modern" | "Legacy" | "Cloud";

export const getLicenseXmlById = async (
  licenseId: string,
  generationMode: ServerLicenseXmlGenerationMode = "Modern"
): Promise<LicenseXmlDto> => {
  const response = await axiosClient.get<LicenseXmlDto>(`${BASE_URL}/${licenseId}/license-xml`, {
    params: {
      generationMode,
    },
  });
  return response.data;
};

export const getServerLicenseBySerial = async (serialId: string): Promise<ServerLicenseDto> => {
  const response = await axiosClient.get<ServerLicenseDto>(`${BASE_URL}/serial/${serialId}`);
  return response.data;
};

export const extendServerLicenseExpiryDate = async (serverLicenseId: string, extendBy: string) => {
  const response = await axiosClient.post<ServerLicenseDto>(`${BASE_URL}/${serverLicenseId}/extend-expiry`, {
    extendBy: extendBy,
  });
  return response.data;
};

export const getHistory = async (serverLicenseId: string): Promise<EntityHistoryReferenceDto[]> => {
  const response = await axiosClient.get(`${BASE_URL}/${serverLicenseId}/history`);
  return response.data;
};

export interface UpdateSalesforceServerLicenseRequest {
  salesforceContractId: string;
  expiryDate: DateTime;
  entitlements: Record<string, unknown>;
}

export interface UpdateZuoraServerLicenseRequest {
  zuoraSubscriptionKey: string;
  nextRenewalDate: DateTime;
  entitlements: Record<string, unknown>;
}

export interface UpdateTrialServerLicenseRequest {
  expiryDate: DateTime;
  entitlements: Record<string, unknown>;
}

export type UpdateServerLicenseFeaturesRequest = {
  entitlements: Record<string, EntitlementDto>;
};

export const updateServerLicenseFeatures = async (
  serverLicenseId: string,
  request: UpdateServerLicenseFeaturesRequest
): Promise<ServerLicenseDto> => {
  const result = put<ServerLicenseDto>(`${serverLicenseId}/features`, request);
  return result;
};

export const updateZuoraServerLicense = async (
  serverLicenseId: string,
  request: UpdateZuoraServerLicenseRequest
): Promise<ServerLicenseDto> => {
  return put<ServerLicenseDto>(`${serverLicenseId}/zuora`, request);
};

export const updateSalesforceServerLicense = async (
  serverLicenseId: string,
  request: UpdateSalesforceServerLicenseRequest
): Promise<ServerLicenseDto> => {
  return put<ServerLicenseDto>(`${serverLicenseId}/salesforce`, request);
};

export const updateTrialServerLicense = async (
  serverLicenseId: string,
  request: UpdateTrialServerLicenseRequest
): Promise<ServerLicenseDto> => {
  return put<ServerLicenseDto>(`${serverLicenseId}/trial`, request);
};

export function isSalesforceContractedLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return licensingChannel.salesforceContractId !== null;
}

export function isTrialServerLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return licensingChannel.trialId !== null;
}

export function isTemporaryServerLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return (
    licensingChannel.expiryDate !== null &&
    !isTrialServerLicensingChannel(licensingChannel) &&
    !isSalesforceContractedLicensingChannel(licensingChannel) &&
    !isZuoraSubscriptionLicensingChannel(licensingChannel) &&
    !isSubscriptionBasedServerLicensingChannel(licensingChannel)
  );
}

export function isZuoraSubscriptionLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return licensingChannel.zuoraSubscriptionKey !== null;
}

export function isSubscriptionBasedServerLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return licensingChannel.subscriptionId !== null;
}

export function isExpiringLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return (
    isSalesforceContractedLicensingChannel(licensingChannel) ||
    isTrialServerLicensingChannel(licensingChannel) ||
    isTemporaryServerLicensingChannel(licensingChannel)
  );
}

export function isEditableLicensingChannel(licensingChannel: ServerLicensingChannelDto): boolean {
  return (
    isSalesforceContractedLicensingChannel(licensingChannel) || isZuoraSubscriptionLicensingChannel(licensingChannel)
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function put<T>(url: string, data: any): Promise<T> {
  return axiosClient.put<T>(`${BASE_URL}/${url}`, convertDateTimesToString(data)).then((response) => response.data);
}
